import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { fetchDeviceGroups } from 'store/thunks/adminDeviceGroupsThunk';
import { IAdminDeviceGroup, TNormalizedDeviceGroups } from 'types';
import { ESliceDataFetchingStatus } from 'types/common';

export interface IAdminDeviceGroupsState {
  groups: IAdminDeviceGroup[];
  normalizedGroups: TNormalizedDeviceGroups;
  sums: {
    normalHashrate: number;
    yesterdayHashrate: number;
    payment: number;
  };
  status: ESliceDataFetchingStatus;
  errorMessage: string | null;
  pagesQuantity: number;
  itemsPerPage: number;
  currentPage: number;
}

const initialState: IAdminDeviceGroupsState = {
  groups: [],
  normalizedGroups: { allIDs: [], byID: {} },
  sums: {
    normalHashrate: 0,
    yesterdayHashrate: 0,
    payment: 0,
  },
  status: ESliceDataFetchingStatus.initial,
  pagesQuantity: 1,
  itemsPerPage: 10,
  currentPage: 0,
  errorMessage: null,
};

export const adminDeviceGroupsSlice = createSlice({
  name: 'adminDeviceGroups',
  initialState,
  reducers: {
    deviceGroupsListSetPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setItemsPerDeviceGroupsListPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = initialState.currentPage;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchDeviceGroups.fulfilled, (state, { payload }) => {
        state.status = ESliceDataFetchingStatus.success;
        const normalizedGroups = payload.content.reduce<
          IAdminDeviceGroupsState['normalizedGroups']
        >(
          (acc, group) => {
            const id = group.id;
            if (!acc.byID[id]) {
              acc.allIDs.push(id);
              acc.byID[id] = group;
            }
            return acc;
          },
          { allIDs: [], byID: {} },
        );
        state.normalizedGroups = normalizedGroups;
        state.groups = payload.content;
        state.sums = payload.sums;
        state.pagesQuantity = payload.details.totalPages;
      })
      .addCase(fetchDeviceGroups.pending, (state) => {
        state.status = ESliceDataFetchingStatus.loading;
        state.errorMessage = null;
      })
      .addCase(fetchDeviceGroups.rejected, (state, action) => {
        state.status = ESliceDataFetchingStatus.error;
        state.errorMessage = action.error.message || 'unknown fetch device groups list error';
      }),
});

export const selectAdminDeviceGroups = (state: RootState) => state.adminDeviceGroups;

export const { deviceGroupsListSetPage, setItemsPerDeviceGroupsListPage } =
  adminDeviceGroupsSlice.actions;

export const adminDeviceGroupsReducer = adminDeviceGroupsSlice.reducer;
